import { all, call, put, delay, select, takeEvery, takeLeading } from 'redux-saga/effects';

import {
  CONFIRM_BANK,
  CREATE_LINK_EVENT,
  CREATE_OBV,
  CREATE_PLAID_LINK,
  EXIT_EVENT,
  BACK_MERCHANT,
  OBV_VERIFICATION,
  ADD_PROPERTIES,
  CONTINUE_OAUTH_CALLBACK
} from './types';

import URls from '../../constants/urls';

import { postRequest } from '../../services/request';
import Configs from './../../config/index';

import {
  CONFIRM_BANK_FAILURE,
  CONFIRM_BANK_SUCCESS,
  CREATE_LINK_EVENT_FAILURE,
  CREATE_LINK_EVENT_SUCCESS,
  CREATE_OBV_FAILURE,
  CREATE_OBV_SUCCESS,
  CREATE_PLAID_LINK_FAILURE,
  CREATE_PLAID_LINK_SUCCESS,
  EXIT_EVENT_FAILURE,
  EXIT_EVENT_SUCCESS,
  BACK_TO_MERCHANT,
  HIDE_LOADING,
  OBV_VERIFICATION_FAILURE,
  OBV_VERIFICATION_SUCCESS,
  SHOW_LOADING,
  SHOW_PLAID_FOOTER,
  HIDE_PLAID_FOOTER,
  ADDING_PROPERTIES,
  CONTINUE_OAUTH_CALLBACK_SUCCESS,
  CONTINUE_OAUTH_CALLBACK_FAILURE,
} from "./reducers.jsx";

import { replaceUrl } from '../../utils/replaceUrl';

import {
  decodeURL,
  isSuccessStatus,
  isVerifiedSuccessfully,
  isPlaidFooterShown,
  sendRedirectUri, getTraceId,
} from '../../utils/common';
import { convertDataLinkEvent, convertMetaDataSuccess } from '../../utils/convertData';
import {
  getLanguage,
  getTransactionInfo,
  getXAuthToken,
  isSessionStorageAccess,
  setTraceParentInSessionStorage,
  setTransactionInfo,
  setXAuthTokenInSessionStorage,
  updateTransactionInfo
} from '../../utils/sessionStorage';
import CONSTANTS from '../../constants/sessionStorageKeys';
import { loadTranslationsFromObvInfo } from '../../i18n/i18n';
import { defaultTheme } from '../../globalStyleds.js';

function* createOBV(action) {
  const theme = action?.OBVInfo?.theme || defaultTheme;
  try {
    isSessionStorageAccess() && setTraceParentInSessionStorage(getTraceId());

    yield put(SHOW_LOADING());
    const response = yield call(postRequest, URls.CREATE_OVB, { code: action.OBVInfo.code }, false);
    const xAuthToken = response.headers[CONSTANTS.X_AUTH_TOKEN];
    const { status, data } = response;

    if (isSuccessStatus(status)) {
      isSessionStorageAccess() && setTransactionInfo({...data, theme});
      isSessionStorageAccess() && setXAuthTokenInSessionStorage(xAuthToken);
      if (data?.properties?.customTexts) {
        loadTranslationsFromObvInfo(data);
      }
      yield put(CREATE_OBV_SUCCESS({
        data, xAuthToken,
        merchantReturnUrl: action.OBVInfo.merchantReturnUrl,
        code: action.OBVInfo.code,
        theme
      }));
    } else {
      yield put(CREATE_OBV_FAILURE({ error: { status, ...data }, theme}));
    }
  } catch (error) {
    yield put(CREATE_OBV_FAILURE({error, theme}));
  } finally {
    yield put(HIDE_LOADING());
  }
}

function* createLinkToken(action) {
  const { obvInfo, xAuthToken, theme } = yield select((state) => state.app);
  const language = getLanguage();
  const linkTokenRequest = {
    redirectUri: sendRedirectUri(obvInfo) ? Configs.REDIRECT_URI : null,
    ...action.deviceData,
    theme,
    language
  };

  const createPlaidLinkUrl = replaceUrl(URls.CREATE_PLAID_LINK, 'clientReferenceId', obvInfo.clientReferenceId);

  try {
    const response = yield call(
      postRequest,
      createPlaidLinkUrl,
      linkTokenRequest,
      xAuthToken,
    );
    const { status, data } = response;

    if (isSuccessStatus(status)) {
      isSessionStorageAccess() && updateTransactionInfo({ merchantReturnUrl: decodeURL(action.merchantReturnUrl), ...data });
      yield put(CREATE_PLAID_LINK_SUCCESS({ data }));
    } else {
      yield put(CREATE_PLAID_LINK_FAILURE({ error: { status, ...data } }));
    }
  } catch (error) {
    yield put(CREATE_PLAID_LINK_FAILURE({ error }));
  } finally {
    yield put(HIDE_LOADING());
  }
}

function* createLinkEvent(action) {
  const { eventName, metadata } = action;
  const stateApp = yield select((state) => state.app);
  let obvInfo = getTransactionInfo() || stateApp.obvInfo;
  try {
    const convertedMetaData = convertDataLinkEvent(metadata);
    if (isPlaidFooterShown(convertedMetaData["view_name"])) {
      yield put(SHOW_PLAID_FOOTER());
    } else {
      yield put(HIDE_PLAID_FOOTER());
    }
    const createLinkEventUrl = replaceUrl(URls.CREATE_LINK_EVENT, 'clientReferenceId', obvInfo.clientReferenceId);
    const response = yield call(
      postRequest,
      createLinkEventUrl,
      {
        eventName,
        metadata: convertedMetaData,
      },
      stateApp.xAuthToken || getXAuthToken(),
    );
    const { status, data } = response;
    if (isSuccessStatus(status)) {
      yield put(CREATE_LINK_EVENT_SUCCESS());
    } else {
      yield put(CREATE_LINK_EVENT_FAILURE({ error: { status, ...data } }));
    }
  } catch (error) {
    yield put(CREATE_LINK_EVENT_FAILURE({ error }));
  }
}

function* continueOAuthCallback(action) {
  const { deviceData, receivedRedirectUri } = action;
  try {
    yield put(SHOW_LOADING());
    const response = yield call(postRequest, URls.LOOKUP_TRANS_DATA, { ...deviceData, receivedRedirectUri });
    const xAuthToken = response.headers[CONSTANTS.X_AUTH_TOKEN];
    const { status, data } = response;
    if (isSuccessStatus(status)) {
      isSessionStorageAccess() && setTraceParentInSessionStorage(data.traceId);
      const rawUrl = data.merchantAppReturnUrl || data.merchantReturnUrl;
      let safeMerchantUrl = '';
      try {
        safeMerchantUrl = decodeURL(rawUrl)
        if(safeMerchantUrl.includes('%')) {
          const secondDecode = decodeURL(safeMerchantUrl);
          if (secondDecode !== safeMerchantUrl) {
            safeMerchantUrl = secondDecode;
          }
        }
      } catch (error) {
        console.error('URL Decoding failed. Using raw string as fallback.', error);
        safeMerchantUrl = rawUrl;
      }
      updateTransactionInfo({
        ...data.obvInfo,
        merchantReturnUrl: safeMerchantUrl,
        token: data.token,
        xAuthToken,
        receivedRedirectUri
      });
      yield put(CONTINUE_OAUTH_CALLBACK_SUCCESS({ ...data, xAuthToken }));
    } else {
      yield put(CONTINUE_OAUTH_CALLBACK_FAILURE({ error: { status, ...data, errorId: '404_DEVICEDATA' }, deviceData }));
    }

  } catch (error) {
    yield put(CONTINUE_OAUTH_CALLBACK_FAILURE({ error: { ...error, errorId: '404_DEVICEDATA' }, deviceData }));
  } finally {
    yield put(HIDE_LOADING());
  }
}

function* exitEvent(action) {
  const { event } = action;
  try {
    const stateApp = yield select((state) => state.app);
    let obvInfo = getTransactionInfo() || stateApp.obvInfo;
    const response = yield call(
      postRequest,
      replaceUrl(URls.EXIT_BANK, 'clientReferenceId', obvInfo.clientReferenceId),
      event,
      stateApp.xAuthToken || getXAuthToken(),
    );
    const { status, data } = response;
    if (isSuccessStatus(status)) {
      yield put(EXIT_EVENT_SUCCESS());
    } else {
      yield put(EXIT_EVENT_FAILURE({ error: { status, ...data } }));
    }
  } catch (error) {
    yield put(EXIT_EVENT_FAILURE({ error }));
  }
}

function* obvVerification(action) {
  yield put(SHOW_LOADING());
  try {
    const stateApp = yield select((state) => state.app);
    let obvInfo = getTransactionInfo() || stateApp.obvInfo;
    const obvVerificationUrl = replaceUrl(URls.OBV_VERIFICATION, 'clientReferenceId', obvInfo.clientReferenceId);
    const response = yield call(
      postRequest,
      obvVerificationUrl,
      convertMetaDataSuccess(action.metadata),
      stateApp.xAuthToken || getXAuthToken(),
    );
    const { status, data } = response;
    if (data && isVerifiedSuccessfully(data.status)) {
      updateTransactionInfo({ ...data });
      yield put(OBV_VERIFICATION_SUCCESS(data));
    } else {
      yield put(OBV_VERIFICATION_FAILURE({ error: { status, ...data } }));
    }
  } catch (error) {
    yield put(OBV_VERIFICATION_FAILURE({ error }));
  } finally {
    yield put(HIDE_LOADING());
  }
}

function* confirmBank(action) {
  yield put(SHOW_LOADING());
  try {
    const stateApp = yield select((state) => state.app);
    const response = yield call(
      postRequest,
      replaceUrl(URls.CONFIRMATION, 'clientReferenceId', action.clientReferenceId),
      null,
      stateApp.xAuthToken || getXAuthToken(),
    );
    const { status, data } = response;
    if (isSuccessStatus(status)) {
      yield put(CONFIRM_BANK_SUCCESS(data));
    } else {
      yield put(CONFIRM_BANK_FAILURE({ error: { status, ...data } }));
    }
  } catch (error) {
    yield put(CONFIRM_BANK_FAILURE({ error }));
  } finally {
    yield delay(30000);
    yield put(HIDE_LOADING());
  }
}

function* addProperties(action) {
  yield put(ADDING_PROPERTIES(action.properties));
}

function* backToMerchant() {
  yield put(BACK_TO_MERCHANT());
}

function* workerSaga() {
  yield takeEvery(CONFIRM_BANK, confirmBank);
  yield takeEvery(CREATE_OBV, createOBV);
  yield takeEvery(CREATE_PLAID_LINK, createLinkToken);
  yield takeEvery(CREATE_LINK_EVENT, createLinkEvent);
  yield takeLeading(CONTINUE_OAUTH_CALLBACK, continueOAuthCallback);
  yield takeEvery(BACK_MERCHANT, backToMerchant);
  yield takeEvery(EXIT_EVENT, exitEvent);
  yield takeEvery(OBV_VERIFICATION, obvVerification);
  yield takeEvery(ADD_PROPERTIES, addProperties);
}

export default function* AppSagas() {
  yield all([workerSaga()]);
}
